/**
 * ECharts theme builder.
 *
 * Reads CSS custom properties from the DOM and builds an ECharts theme
 * object that matches the app's current light/dark/neon appearance. The
 * built theme is registered with `echarts.registerTheme()` and cached so
 * subsequent renders are free — the cache is invalidated when the resolved
 * theme or variant changes.
 */

import { echarts } from './echarts-setup';

/* ── HSL resolution ─────────────────────────────────────────────── */

/**
 * Parse an HSL triple stored as a CSS custom property ("199 89% 48%")
 * and return a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Read a CSS custom property from `:root` and convert the `H S% L%`
 * value to a hex color string.
 */
export function resolveHsl(varName: string): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!raw) return '#888888';
  const parts = raw.split(/\s+/);
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]);
  const l = parseFloat(parts[2]);
  if (Number.isNaN(h) || Number.isNaN(s) || Number.isNaN(l)) return '#888888';
  return hslToHex(h, s, l);
}

/* ── Theme builder ──────────────────────────────────────────────── */

function buildChartTheme() {
  const palette = [
    resolveHsl('--chart-1'),
    resolveHsl('--chart-2'),
    resolveHsl('--chart-3'),
    resolveHsl('--chart-4'),
    resolveHsl('--chart-5'),
  ];
  const fg = resolveHsl('--foreground');
  const mutedFg = resolveHsl('--muted-foreground');
  const border = resolveHsl('--border');
  const card = resolveHsl('--card');

  const axisCommon = {
    axisLine: { lineStyle: { color: border } },
    axisTick: { lineStyle: { color: border } },
    axisLabel: { color: mutedFg, fontSize: 11 },
    splitLine: { lineStyle: { color: border, type: 'dashed' as const } },
  };

  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: {
      color: fg,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 12,
      textBorderWidth: 0,
    },
    title: {
      textStyle: { color: fg, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: mutedFg, fontSize: 12 },
    },
    legend: {
      bottom: 0,
      textStyle: { color: mutedFg, fontSize: 11 },
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      itemStyle: { borderWidth: 0, color: 'inherit' },
      backgroundColor: card,
      borderRadius: 6,
      padding: [6, 10],
    },
    tooltip: {
      backgroundColor: card,
      borderColor: border,
      textStyle: { color: fg, fontSize: 12 },
      extraCssText: 'border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);',
    },
    categoryAxis: axisCommon,
    valueAxis: axisCommon,
    logAxis: axisCommon,
    timeAxis: axisCommon,
    line: {
      smooth: false,
      symbolSize: 4,
      lineStyle: { width: 2 },
      label: { color: fg, textBorderWidth: 0 },
      markPoint: { label: { color: '#fff', textBorderWidth: 0 } },
      markLine: {
        label: {
          color: '#1a1a1a',
          textBorderColor: '#fff',
          textBorderWidth: 2,
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 3,
          padding: [2, 6],
        },
      },
    },
    bar: {
      itemStyle: { borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 48,
      label: { color: fg, textBorderWidth: 0 },
    },
    scatter: {
      label: { color: fg, textBorderWidth: 0 },
    },
    pie: {
      center: ['50%', '45%'],
      itemStyle: { borderWidth: 2, borderColor: card },
      label: { color: fg, textBorderWidth: 0 },
    },
    radar: {
      axisName: { color: mutedFg },
      splitLine: { lineStyle: { color: border } },
      splitArea: { areaStyle: { color: ['transparent', 'transparent'] } },
    },
    dataZoom: {
      borderColor: border,
      textStyle: { color: mutedFg },
    },
    treemap: {
      breadcrumb: { show: false },
      label: { color: '#fff', textBorderWidth: 0 },
      // Treemaps are self-labeling; legend is redundant and can show
      // invisible icons. Agents can re-enable with legend: { show: true }.
    },
    map: {
      itemStyle: {
        areaColor: resolveHsl('--muted'),
        borderColor: border,
        borderWidth: 0.5,
      },
      emphasis: {
        itemStyle: { areaColor: resolveHsl('--accent') },
        label: { color: fg },
      },
      label: { color: mutedFg, fontSize: 10 },
    },
    geo: {
      itemStyle: {
        areaColor: resolveHsl('--muted'),
        borderColor: border,
      },
    },
    graph: {
      color: palette,
    },
  };
}

/* ── Registration + cache ───────────────────────────────────────── */

const themeCache = new Map<string, boolean>();

/**
 * Register (or return cached) the ECharts theme for the current CSS state.
 * Returns the theme name to pass to `ReactECharts`.
 */
export function registerChartTheme(
  resolvedTheme: string,
  variant: string,
): string {
  const name = `shadcn-${resolvedTheme}-${variant}`;
  if (!themeCache.has(name)) {
    const theme = buildChartTheme();
    echarts.registerTheme(name, theme);
    themeCache.set(name, true);
  }
  return name;
}

/** Force theme cache invalidation (called on theme switch). */
export function invalidateChartThemeCache(): void {
  themeCache.clear();
}
